import classNames         from 'classnames';
import moment, { Moment } from 'moment';
import React, { FC }      from 'react';
import { Cell, Grid }     from "styled-css-grid";
import { GridProps }      from 'semantic-ui-react';

import { TimeService }                      from '../../../../../services';
import { CorvalUIColor, NumberRange0to100 } from '../../../../../types';
import { StyledContainer }                  from '../../../../containers';

interface RuledTimeLineGridProps {
	/* Date to start the ruled grid, will use date to calculate start of month */
	startDate: Moment,

	/* Date to end the ruled grid, will consider total count of months from start */
	endDate: Moment,

	/* Whether to adjust the start date given to align with the first tick mark increment for the freq (month/year) or not */
	alignStartDate?: boolean,

	/* Show labels on the grid? */
	showLabels?: boolean,

	/* Show a tiny tick mark for mid month dates? Does not work with the mergeFreqTicks prop */
	showMidMonth?: boolean,

	/* What frequency does the grid have in aggregating divisions between the dates,
	eg: frequency of 3 would be show a rule every 3 months */
	labelFreqIncrement?: number,

	/* Formatter function for the labels */
	labelFormatter?( m: Moment ): string,

	/* Merge the grid cells or produce empty ones without labels as well? */
	mergeFreqTicks?: boolean,

	/* Text align the labels centered? */
	centerLabels?: boolean,

	/* Calculates the grid using the differential of months instead days */
	useMonthDifferential?: boolean,

	/* Calculates the grid using the differential of months instead days */
	useYearDifferential?: boolean,

	/* Opacity level of the rule lines */
	ruleOpacity?: NumberRange0to100,

	/* Grid props to pass to top level component */
	gridProps?: GridProps,

	[ additionalProps: string ]: any
}

const defaultProps: RuledTimeLineGridProps = {
	startDate:            moment(),
	endDate:              moment(),
	alignStartDate:       false,
	showLabels:           false,
	showMidMonth:         false,
	labelFreqIncrement:   1,
	labelFormatter:       null,
	mergeFreqTicks:       false,
	centerLabels:         false,
	useMonthDifferential: false,
	useYearDifferential:  false,
	ruleOpacity:          100,
	gridProps:            {},
}

const RuledTimeLineGrid: FC<RuledTimeLineGridProps> = ( props ) => {

	let { startDate: _startDate, endDate, labelFreqIncrement } = props,
	    { mergeFreqTicks, showMidMonth }                       = props,
	    { useMonthDifferential }                               = props,
	    { useYearDifferential }                                = props;

	let differentialUnit   = ( useYearDifferential ? 'year' : 'month' ) as moment.unitOfTime.StartOf,
	    differentialMethod = useMonthDifferential ? 'getMonthDifferential'
	                                              : useYearDifferential ? 'getYearDifferential'
	                                                                    : 'getDayDifferential';

	let startDate = props.alignStartDate ? _startDate.clone().startOf( differentialUnit ) : _startDate;

	let gridColumns = TimeService[ differentialMethod ]( startDate, endDate ),
	    unitMarks   = TimeService.getDatesWithinRange( startDate, endDate, labelFreqIncrement, useYearDifferential ? 'year' : 'month' ),
	    tickMarks   = [ ...unitMarks ];

	if( !useYearDifferential && showMidMonth ){
		unitMarks.map( monthMarkDate => {
			tickMarks.push( monthMarkDate.clone().add( 15, 'days' ) )
		} )
	}

	return (

		<Grid columns={ gridColumns } gap={ '0px' } { ...props.gridProps }>
			<>

				{/* Month Ticks */ }
				{ tickMarks.map( ( tickMarkDate, index ) => {

					let comparatorDate = tickMarkDate.clone().startOf( differentialUnit ),
					    isUnitLabel    = tickMarkDate.isSame( comparatorDate ),
					    column         = TimeService[ differentialMethod ]( startDate, isUnitLabel ? tickMarkDate.clone().add( 1, 'day' ) : tickMarkDate ),
					    label          = isUnitLabel ? props.labelFormatter ? ( props.labelFormatter( tickMarkDate ) ) : tickMarkDate.format( 'MMM' ).toUpperCase() : null,
					    skipTick       = ( !label && !showMidMonth && mergeFreqTicks );

					if( startDate.isSame( tickMarkDate, 'day' ) ){
						column = 0;
					}

					if( skipTick ) return null;

					return (

						<Cell key={ index }
						      top={ 1 }
						      left={ column + 1 }
						      height={ 1 }
						      width={ mergeFreqTicks ? labelFreqIncrement : 1 }
						      className={ 'position--relative z-index--100' }>

							<StyledContainer fill={ 'area' }
							                 style={ { top: isUnitLabel ? 0 : '50%', left: 0 } }
							                 borderColor={ isUnitLabel ? CorvalUIColor.DK_SCREEN_50 : CorvalUIColor.DK_SCREEN_25 }
							                 className={ classNames( [
								                 'border-sm-left',
								                 'text--xs',
								                 'text--colored-lightBlue',
								                 'text--strong',
								                 `opacity--${ props.ruleOpacity }`
							                 ], {
								                 'padding-xs-left': !props.centerLabels,
								                 'text-center':     props.centerLabels,
							                 } ) }>

								{ props.showLabels && isUnitLabel && label }

							</StyledContainer>
						</Cell>

					)
				} ) }

				{ props.children }

			</>

		</Grid>
	);
};

RuledTimeLineGrid.defaultProps = defaultProps;

export { RuledTimeLineGrid }
